import { useMemo, useState } from "react";
import { Avatar, Box, Button, Stack, Tooltip, Typography } from "@mui/material";

import { initials } from "../lib/initials.ts";

// Ported from the tasks monolith's EpicGanttChart: a sliding N-month window,
// one lane per row of non-overlapping bars, a progress fill inside each bar,
// and a diamond for an epic that carries only one date. The geometry is the
// same; MUI's `sx` stands in for the original's Tailwind classes.

const WINDOW_MONTHS = 6;
const LANE_HEIGHT = 26; // bar height (20) + 6px gap

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/**
 * Greedy lane assignment: sort by effective start, then give each epic the
 * first lane whose last bar already ended. Returns `{epic, lane}` in the
 * input order, so React keys stay stable across a reorder.
 */
function assignLanes(epics) {
  const effective = epics.map((e) => {
    const start = e.startDate ? new Date(`${e.startDate}T00:00:00`).getTime() : new Date(e.createdAt || Date.now()).getTime();
    const end = e.targetDate ? new Date(`${e.targetDate}T00:00:00`).getTime() : start;
    return { epic: e, start, end: Math.max(end, start) };
  });
  const order = effective.map((x, i) => ({ ...x, i })).sort((a, b) => a.start - b.start || a.i - b.i);
  const laneTails = [];
  const laneByIdx = new Map();
  for (const item of order) {
    let lane = laneTails.findIndex((tail) => tail <= item.start);
    if (lane === -1) { lane = laneTails.length; laneTails.push(item.end); }
    else laneTails[lane] = item.end;
    laneByIdx.set(item.i, lane);
  }
  return effective.map((x, i) => ({ epic: x.epic, lane: laneByIdx.get(i) ?? 0 }));
}

function MilestoneBar({ epic, owner, fallbackColor, windowStart, windowEnd, windowMs, lane, selected, onClick }) {
  const winStart = windowStart.getTime();
  const winEnd = windowEnd.getTime();
  const pct = (ms) => ((ms - winStart) / windowMs) * 100;

  const explicitStart = epic.startDate ? new Date(`${epic.startDate}T00:00:00`).getTime() : null;
  const end = epic.targetDate ? new Date(`${epic.targetDate}T00:00:00`).getTime() : null;
  const start = explicitStart ?? (epic.createdAt ? new Date(epic.createdAt).getTime() : null);
  if (!start && !end) return null;

  const isDiamond = !end || !start || start >= end;
  const top = 4 + lane * LANE_HEIGHT;
  const color = epic.color || fallbackColor || "#1f7a3d";
  const title = [
    epic.name,
    epic.startDate && epic.targetDate ? `${epic.startDate} → ${epic.targetDate}` : null,
    `${epic.counts?.percentComplete ?? 0}% complete · ${epic.counts?.done ?? 0}/${epic.counts?.total ?? 0} tasks`,
    owner ? `Owner: ${owner.displayName || owner.emails?.[0]}` : null,
  ].filter(Boolean).join("\n");

  if (isDiamond) {
    const at = start ?? end;
    if (at < winStart || at > winEnd) return null;
    return (
      <Tooltip title={title}>
        <Box component="button" type="button" onClick={onClick}
          aria-label={`${epic.name}, milestone`}
          sx={{
            position: "absolute", left: `${pct(at)}%`, top, width: 14, height: 14,
            bgcolor: color, borderRadius: "2px", border: 0, cursor: "pointer", p: 0,
            transform: "translateX(-50%) rotate(45deg)",
            outline: selected ? "2px solid" : "none", outlineColor: "text.primary",
            outlineOffset: selected ? "2px" : 0,
          }} />
      </Tooltip>
    );
  }

  const clampedStart = Math.max(start, winStart);
  const clampedEnd = Math.min(end, winEnd);
  if (clampedEnd < winStart || clampedStart > winEnd) return null;
  const left = pct(clampedStart);
  const width = pct(clampedEnd) - left;
  const fillPct = Math.max(0, Math.min(100, epic.counts?.percentComplete ?? 0));

  return (
    <Tooltip title={title}>
      <Box component="button" type="button" onClick={onClick}
        aria-label={`Open epic ${epic.name}`}
        sx={{
          position: "absolute", left: `${left}%`, top, width: `${width}%`, height: 20,
          borderRadius: 1, border: "1px solid", borderColor: color, cursor: "pointer",
          bgcolor: `${color}33`, p: 0, textAlign: "left", overflow: "hidden",
          outline: selected ? "2px solid" : "none", outlineColor: "text.primary",
          "&:hover": { boxShadow: 1 },
        }}>
        <Box sx={{ position: "absolute", inset: 0, width: `${fillPct}%`, bgcolor: color, opacity: 0.55 }} />
        <Stack direction="row" spacing={0.5} sx={{ position: "relative", alignItems: "center", height: "100%", px: 0.75 }}>
          {owner ? (
            <Avatar sx={{ width: 14, height: 14, fontSize: 8, flexShrink: 0 }}>
              {initials(owner.displayName || owner.emails?.[0])}
            </Avatar>
          ) : null}
          <Typography variant="caption" noWrap sx={{ fontSize: 10, fontWeight: 600 }}>{epic.name}</Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
}

/** A single-row Gantt (no project grouping) for one project's epics. */
export default function EpicGantt({ epics, peopleById, fallbackColor, selectedEpicId, onEpicClick, emptyMessage }) {
  const [windowStart, setWindowStart] = useState(() => startOfMonth(new Date()));
  const windowEnd = useMemo(() => addMonths(windowStart, WINDOW_MONTHS), [windowStart]);
  const windowMs = windowEnd.getTime() - windowStart.getTime();

  const datedEpics = useMemo(() => epics.filter((e) => e.startDate || e.targetDate), [epics]);

  const monthTicks = useMemo(() => {
    const out = [];
    for (let i = 0; i < WINDOW_MONTHS; i += 1) {
      const d = addMonths(windowStart, i);
      out.push({ key: i, label: d.toLocaleDateString(undefined, { month: "short", year: "numeric" }), left: (i / WINDOW_MONTHS) * 100 });
    }
    return out;
  }, [windowStart]);

  const todayPct = useMemo(() => {
    const now = Date.now();
    if (now < windowStart.getTime() || now > windowEnd.getTime()) return null;
    return ((now - windowStart.getTime()) / windowMs) * 100;
  }, [windowStart, windowEnd, windowMs]);

  const assigned = useMemo(() => assignLanes(datedEpics), [datedEpics]);
  const laneCount = Math.max(1, ...assigned.map(({ lane }) => lane + 1));
  const rowHeight = laneCount * LANE_HEIGHT + 10;

  const move = (months) => setWindowStart((d) => addMonths(d, months));
  const goToday = () => setWindowStart(startOfMonth(new Date()));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button size="small" variant="outlined" onClick={() => move(-1)} aria-label="Earlier">&#8592;</Button>
        <Button size="small" variant="outlined" onClick={goToday}>Today</Button>
        <Button size="small" variant="outlined" onClick={() => move(1)} aria-label="Later">&#8594;</Button>
        <Typography variant="body2" sx={{ fontWeight: 600, ml: 1 }}>
          {windowStart.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
          {" – "}
          {addMonths(windowEnd, -1).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
        </Typography>
      </Stack>

      {datedEpics.length === 0 ? (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage || "No milestones in this window. Set start / target dates on an epic to plot it here."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ position: "relative", height: 28, borderBottom: 1, borderColor: "divider" }}>
            {monthTicks.map((t) => (
              <Typography key={t.key} variant="caption" color="text.secondary"
                sx={{ position: "absolute", left: `${t.left}%`, top: 0, px: 1, lineHeight: "28px",
                      textTransform: "uppercase", letterSpacing: 0.3, borderLeft: t.left ? 1 : 0, borderColor: "divider" }}>
                {t.label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ position: "relative", minHeight: rowHeight }}>
            {monthTicks.map((t) => (
              <Box key={`grid-${t.key}`} sx={{ position: "absolute", left: `${t.left}%`, top: 0, bottom: 0, width: "1px", bgcolor: "divider", opacity: 0.6 }} />
            ))}
            {todayPct !== null ? (
              <Box sx={{ position: "absolute", left: `${todayPct}%`, top: 0, bottom: 0, width: "2px", bgcolor: "primary.main", opacity: 0.7 }} />
            ) : null}
            {assigned.map(({ epic, lane }) => (
              <MilestoneBar key={epic.id} epic={epic} owner={peopleById.get(epic.ownerId)}
                fallbackColor={fallbackColor} windowStart={windowStart} windowEnd={windowEnd} windowMs={windowMs}
                lane={lane} selected={epic.id === selectedEpicId} onClick={() => onEpicClick(epic)} />
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
